import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Spinner,
  Alert,
  Pagination,
  Form,
  InputGroup,
} from "react-bootstrap";
import {
  ArrowLeft,
  BoxArrowRight,
  Bell,
  PersonCircle,
  Trash3,
  EnvelopeAt,
  Search,
} from "react-bootstrap-icons";
import SettingsGear from "../components/SettingsGear";
import axios from "axios";
import * as XLSX from "xlsx";
import "../App.css";

import LOGO_URL from "../img/tmis_logo_orig.png";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_USERS = `${API_BASE}/getUsers`;
const ENDPOINT_REMOVE_NON_TG = `${API_BASE}/removeNonTG`;
const ENDPOINT_REGISTER_WITH_US = `${API_BASE}/registerWithUs`;

const NAV_BLUE = "#183B72";
const PANEL_BG = "#F1F3F6";
const PANEL_SURFACE = "#E7EAEE";

function safeParse(json, fallback = {}) {
  try {
    return JSON.parse(json || "") ?? fallback;
  } catch {
    return fallback;
  }
}

function getStoredUser() {
  const user = safeParse(localStorage.getItem("user"), {});
  const displayName =
    localStorage.getItem("display_name") ||
    user.display_name ||
    user.username ||
    localStorage.getItem("username") ||
    "User Acc";
  const userId = user.user_id ?? user.id ?? "";
  return { displayName, userId };
}

function toDisplayDate(v) {
  if (!v) return "-";
  if (/^\d{2}-\d{2}-\d{4}$/.test(String(v))) return v;

  let m = String(v).match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (!isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    }
  }

  m = String(v).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    if (!isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    }
  }

  const d = new Date(String(v));
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return String(v);
}

function parseDisplayDateToTime(v) {
  if (!v || v === "-") return 0;
  const s = String(v).trim();
  const m = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    if (!isNaN(d.getTime())) return d.getTime();
  }
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.getTime();
  return 0;
}

function statusStyles(statusRaw) {
  const s = String(statusRaw || "").toLowerCase();
  if (s === "registered") {
    return {
      bg: "rgba(16,185,129,.15)",
      border: "1px solid rgba(16,185,129,.35)",
      color: "#065f46",
    };
  }
  if (s === "unregister" || s === "unregistered") {
    return {
      bg: "rgba(239,68,68,.12)",
      border: "1px solid rgba(239,68,68,.35)",
      color: "#991b1b",
    };
  }
  if (s.includes("pending") || s.includes("review") || s === "invited") {
    return {
      bg: "rgba(245,158,11,.14)",
      border: "1px solid rgba(245,158,11,.38)",
      color: "#92400e",
    };
  }
  return {
    bg: "rgba(29,78,216,.10)",
    border: "1px solid rgba(29,78,216,.28)",
    color: "#1e3a8a",
  };
}

/**
 * SEARCH HELPER
 */
function filterUnregisterRows(rows, search) {
  const termRaw = (search || "").trim();
  const term = termRaw.toLowerCase();
  if (!term) return rows;

  const isNumeric = /^\d+$/.test(termRaw);
  const looksLikeEmail = term.includes("@");

  const norm = (val) =>
    String(val ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const isValid = (v) => v && v !== "-" && v !== "n/a";

  const getValues = (r) => [
    r.name,
    r.phone_number,
    r.email,
    r.tg_expiry_date,
    r.badge_no,
    r.status_name,
    r.account_type,
    r.created_date,
  ];

  // 1) exact email match if input looks like an email
  if (looksLikeEmail) {
    const exactEmail = rows.filter((r) =>
      isValid(r.email) ? norm(r.email) === term : false
    );
    if (exactEmail.length > 0) return exactEmail;
  }

  // 2) exact numeric match for badge_no / phone_number
  if (isNumeric) {
    const exactNumeric = rows.filter((r) => {
      const badge = String(r.badge_no ?? "").trim();
      const phone = String(r.phone_number ?? "").trim();
      return badge === termRaw || phone === termRaw;
    });
    if (exactNumeric.length > 0) return exactNumeric;
  }

  // 3) generic exact match on ANY column
  const exactMatches = rows.filter((r) =>
    getValues(r).some((val) => {
      const v = norm(val);
      if (!isValid(v)) return false;
      return v === term;
    })
  );
  if (exactMatches.length > 0) return exactMatches;

  // 4) fallback: contains search on ANY column
  return rows.filter((r) =>
    getValues(r).some((val) => {
      const v = norm(val);
      if (!isValid(v)) return false;
      return v.includes(term);
    })
  );
}

export default function Unregister() {
  const { displayName } = useMemo(getStoredUser, []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [existList, setExistList] = useState([]);

  const [selected, setSelected] = useState(() => new Set());

  const [deleting, setDeleting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "asc",
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [search, setSearch] = useState("");

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("auth_token") ||
    "";

  const axiosHeaders = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace("/");
  };

  const handleBack = () => {
    if (window.history.length > 1) window.history.back();
    else window.location.replace("/");
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    setActionMsg("");
    setExistList([]);
    try {
      if (!API_BASE) {
        setRows([]);
        return;
      }

      const { data } = await axios.get(ENDPOINT_USERS, {
        headers: axiosHeaders,
        timeout: 20000,
      });

      if (data?.success && Array.isArray(data?.data)) {
        const mapped = data.data.map((r, idx) => ({
          id: r.id ?? idx + 1,
          name: r.user_name || "-",
          phone_number: r.phone_number || "-",
          email: r.email || "-",
          tg_expiry_date: toDisplayDate(r.tg_expiry_date),
          badge_no: r.badge_no || "-",
          status_name: r.status_name || "-",
          account_type: r.account_type || "-",
          created_date: toDisplayDate(
            r.created_at || r.created_date || r.createdDate
          ),
        }));
        setRows(mapped);
      } else {
        setRows([]);
        setErr(data?.message || "Failed to load data.");
      }
    } catch (e) {
      setRows([]);
      setErr("Server error. Please try again later.");
    } finally {
      setLoading(false);
      setSelected(new Set());
      setSortConfig({ key: "", direction: "asc" });
      setPage(1);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  // 🔍 use the helper
  const filteredRows = useMemo(
    () => filterUnregisterRows(rows, search),
    [rows, search]
  );

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;

    const { key, direction } = sortConfig;
    const dir = direction === "asc" ? 1 : -1;
    const clone = [...filteredRows];

    clone.sort((a, b) => {
      if (key === "id") {
        const va = Number(a.id) || 0;
        const vb = Number(b.id) || 0;
        return (va - vb) * dir;
      }

      if (key === "tg_expiry_date" || key === "created_date") {
        const ta = parseDisplayDateToTime(a[key]);
        const tb = parseDisplayDateToTime(b[key]);
        return (ta - tb) * dir;
      }

      const va = (a[key] ?? "").toString().toLowerCase();
      const vb = (b[key] ?? "").toString().toLowerCase();

      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });

    return clone;
  }, [filteredRows, sortConfig]);

  const totalRows = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil((totalRows || 1) / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIndex = (page - 1) * pageSize;
  const currentPageRows = useMemo(
    () => sortedRows.slice(startIndex, startIndex + pageSize),
    [sortedRows, startIndex, pageSize]
  );

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllCurrentPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected =
        currentPageRows.length > 0 &&
        currentPageRows.every((r) => next.has(r.id));

      if (allSelected) {
        currentPageRows.forEach((r) => next.delete(r.id));
      } else {
        currentPageRows.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  const allCurrentPageSelected =
    currentPageRows.length > 0 &&
    currentPageRows.every((r) => selected.has(r.id));

  const selectedRows = rows.filter((r) => selected.has(r.id));

  const firstRowNo = totalRows === 0 ? 0 : Math.min(startIndex + 1, totalRows);
  const lastRowNo =
    totalRows === 0
      ? 0
      : Math.min(startIndex + currentPageRows.length, totalRows);

  const handleDelete = async () => {
    if (selectedRows.length === 0 || deleting) return;

    const ok = window.confirm(`Delete ${selectedRows.length} record(s)?`);
    if (!ok) return;

    setDeleting(true);
    setActionMsg("");
    setErr("");
    setExistList([]);

    try {
      const payload = {
        user: selectedRows.map((r) => ({ nonUserId: r.id })),
      };

      const { data } = await axios.post(ENDPOINT_REMOVE_NON_TG, payload, {
        headers: axiosHeaders,
        timeout: 20000,
      });

      if (data?.success) {
        const deletedIds = new Set(selectedRows.map((r) => r.id));
        setRows((prev) => prev.filter((r) => !deletedIds.has(r.id)));
        setSelected(new Set());
        setActionMsg("Selected record(s) removed successfully.");
      } else {
        setErr(data?.message || "Failed to remove. Please try again.");
      }
    } catch (e) {
      setErr("Server error while removing. Please try again later.");
    } finally {
      setDeleting(false);
    }
  };

  const handleSendEmail = async () => {
    if (selectedRows.length === 0 || emailing) return;

    setEmailing(true);
    setActionMsg("");
    setErr("");
    setExistList([]);

    try {
      const toInvite = selectedRows
        .filter((r) => r.email && String(r.email).includes("@"))
        .map((r) => ({
          nonUserId: r.id,
          email: r.email,
          badgeNo: r.badge_no || r.badgeNo || "",
        }));

      if (toInvite.length === 0) {
        setErr("No valid email addresses in the selected rows.");
        return;
      }

      const payload = { unregisteredUser: toInvite };

      const { data } = await axios.post(ENDPOINT_REGISTER_WITH_US, payload, {
        headers: axiosHeaders,
        timeout: 30000,
      });

      if (data?.success) {
        setActionMsg(data?.message || "Invitation sent.");
        const exists = Array.isArray(data?.data) ? data.data : [];
        setExistList(exists);

        const invitedIds = new Set(toInvite.map((x) => x.nonUserId));
        setRows((prev) =>
          prev.map((r) =>
            invitedIds.has(r.id) ? { ...r, status_name: "Invited" } : r
          )
        );

        setSelected(new Set());
      } else {
        setErr(data?.message || "Failed to send invitations.");
      }
    } catch (e) {
      setErr("Server error while sending invitations. Please try again later.");
    } finally {
      setEmailing(false);
    }
  };

  const handleExport = () => {
    if (selectedRows.length === 0 || exporting) {
      setErr("Please select at least one record to export.");
      return;
    }

    setExporting(true);
    setErr("");
    setActionMsg("");

    try {
      const header = [
        "No",
        "Name",
        "Phone Number",
        "Email",
        "TG License Expiry Date",
        "Badge Number",
        "Status",
        "Created Date",
      ];

      const dataRows = selectedRows.map((r, index) => [
        index + 1,
        String(r.name || "")
          .toLowerCase()
          .replace(/(^|[\s\-'])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase()),
        r.phone_number || "",
        String(r.email || "").toLowerCase(),
        r.tg_expiry_date || "",
        r.badge_no || "",
        r.status_name || "",
        r.created_date || "",
      ]);

      const worksheetData = [header, ...dataRows];

      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      ws["!cols"] = [8, 32, 18, 36, 24, 16, 14, 22].map((wch) => ({ wch }));
      ws["!rows"] = worksheetData.map((_, i) => ({ hpt: i === 0 ? 30 : 24 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Unregistered_TG");

      XLSX.writeFile(wb, "unregistered_tg_selected.xlsx");

      setActionMsg(
        `Exported ${selectedRows.length} selected record(s) to Excel file.`
      );
    } catch (e) {
      console.error("Export error:", e);
      setErr("Failed to export. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      className="custom-font"
      style={{ minHeight: "100vh", background: "#f7f9fc" }}
    >
      <Container>
        <div
          className="d-flex align-items-center justify-content-between py-2"
          style={{ gap: 16 }}
        >
          <img src={LOGO_URL} alt="TMIS" style={{ height: 50 }} />
          <div className="d-flex align-items-center" style={{ gap: 25 }}>
            <Bell size={20} color="#1A3584" />
            <SettingsGear />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
              aria-label="Logout"
            />
            <span>{displayName}</span>
            <PersonCircle size={23} color="#1A3584" />
          </div>
        </div>
      </Container>

      <div
        style={{
          height: 64,
          background: NAV_BLUE,
          color: "white",
          display: "flex",
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <Container>
          <div
            className="container-xxl d-flex align-items-center"
            style={{ gap: 12 }}
          >
            <span
              role="button"
              aria-label="Go back"
              tabIndex={0}
              onClick={handleBack}
              onKeyDown={(e) =>
                e.key === "Enter" || e.key === " " ? handleBack() : null
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                background: "transparent",
                cursor: "pointer",
                outline: "none",
              }}
              className="back-icon"
            >
              <ArrowLeft size={18} color="#ffffff" />
            </span>
            <h4 className="m-0 fw-bold text-uppercase">
              UNREGISTERED TOURIST GUIDE
            </h4>
          </div>
        </Container>
      </div>

      <Container style={{ paddingTop: 22, paddingBottom: 40 }}>
        <Row className="g-4">
          <Col xs={12}>
            <Card
              style={{
                background: PANEL_BG,
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
                borderRadius: 18,
              }}
            >
              <Card.Body style={{ background: "white", borderRadius: 16 }}>
                {actionMsg ? (
                  <Alert variant="success" className="py-2">
                    {actionMsg}
                  </Alert>
                ) : null}
                {err ? (
                  <Alert variant="danger" className="py-2">
                    {err}
                  </Alert>
                ) : null}
                {existList.length > 0 && (
                  <Alert variant="warning" className="py-2">
                    The following emails already exist:
                    <div className="mt-2">
                      <Table size="sm" bordered responsive className="mb-0">
                        <thead>
                          <tr>
                            <th style={{ whiteSpace: "nowrap" }}>Email</th>
                            <th style={{ whiteSpace: "nowrap" }}>Badge No</th>
                          </tr>
                        </thead>
                        <tbody>
                          {existList.map((x, i) => (
                            <tr key={i}>
                              <td>{x.email}</td>
                              <td>{x.badgeNo}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Alert>
                )}

                <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-nowrap">
                  <div className="d-flex gap-2 flex-nowrap">
                    <Button
                      variant="outline-danger"
                      className="d-inline-flex align-items-center gap-2"
                      onClick={handleDelete}
                      disabled={selected.size === 0 || deleting}
                      title={
                        selected.size === 0
                          ? "Select rows to delete"
                          : deleting
                          ? "Removing..."
                          : "Delete selected"
                      }
                    >
                      {deleting ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Trash3 size={16} />
                      )}
                      {deleting ? "Removing..." : "Delete"}
                    </Button>

                    <Button
                      variant="primary"
                      className="d-inline-flex align-items-center gap-2"
                      onClick={handleSendEmail}
                      disabled={selected.size === 0 || emailing}
                      title={
                        selected.size === 0
                          ? "Select rows to email"
                          : emailing
                          ? "Sending..."
                          : "Send invitation to selected"
                      }
                    >
                      {emailing ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <EnvelopeAt size={16} />
                      )}
                      {emailing ? "Sending..." : "Send Email"}
                    </Button>
                  </div>

                  <div className="d-flex align-items-center gap-3 flex-nowrap">
                    <InputGroup size="sm" style={{ width: 260 }}>
                      <InputGroup.Text>
                        <Search size={14} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search all columns..."
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setPage(1);
                        }}
                      />
                    </InputGroup>

                    <div className="d-flex align-items-center gap-1">
                      <span className="small text-muted">Rows per page:</span>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value) || 50);
                          setPage(1);
                        }}
                        className="form-select form-select-sm"
                        style={{ width: "auto" }}
                      >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>

                    <Button
                      variant="success"
                      className="d-inline-flex align-items-center gap-2"
                      onClick={handleExport}
                      disabled={exporting}
                      title="Export selected rows to Excel"
                    >
                      {exporting && <Spinner animation="border" size="sm" />}
                      {exporting ? "Exporting..." : "Export to Excel"}
                    </Button>
                  </div>
                </div>

                <div className="table-responsive">
                  <Table hover align="start" className="mb-0">
                    <thead
                      style={{
                        background: PANEL_SURFACE,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <tr>
                        <th style={{ width: 44 }}>
                          <input
                            type="checkbox"
                            checked={allCurrentPageSelected}
                            onChange={toggleAllCurrentPage}
                            aria-label="Select all on this page"
                          />
                        </th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("id")}
                        >
                          No{renderSortIndicator("id")}
                        </th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("name")}
                        >
                          Name{renderSortIndicator("name")}
                        </th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("phone_number")}
                        >
                          Phone Number
                          {renderSortIndicator("phone_number")}
                        </th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("email")}
                        >
                          Email{renderSortIndicator("email")}
                        </th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("tg_expiry_date")}
                        >
                          TG License Expiry Date
                          {renderSortIndicator("tg_expiry_date")}
                        </th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("badge_no")}
                        >
                          Badge Number
                          {renderSortIndicator("badge_no")}
                        </th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("status_name")}
                        >
                          Status{renderSortIndicator("status_name")}
                        </th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("created_date")}
                        >
                          Created Date
                          {renderSortIndicator("created_date")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={9} className="text-center py-4">
                            <Spinner size="sm" className="me-2" /> Loading…
                          </td>
                        </tr>
                      ) : currentPageRows.length ? (
                        currentPageRows.map((r, idx) => {
                          const st = statusStyles(r.status_name);
                          return (
                            <tr key={r.id}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selected.has(r.id)}
                                  onChange={() => toggleRow(r.id)}
                                  aria-label={`Select row ${
                                    startIndex + idx + 1
                                  }`}
                                />
                              </td>
                              <td>{startIndex + idx + 1}</td>
                              <td>{r.name}</td>
                              <td>{r.phone_number}</td>
                              <td>{r.email}</td>
                              <td>{r.tg_expiry_date}</td>
                              <td>{r.badge_no}</td>
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    background: st.bg,
                                    border: st.border,
                                    color: st.color,
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {r.status_name}
                                </span>
                              </td>
                              <td>{r.created_date}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={9} className="text-center py-4">
                            No data found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {totalRows > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="small text-muted">
                      Showing {firstRowNo}–{lastRowNo} of {totalRows} rows
                    </div>
                    <Pagination size="sm" className="mb-0">
                      <Pagination.First
                        disabled={page === 1}
                        onClick={() => setPage(1)}
                      />
                      <Pagination.Prev
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      />
                      <Pagination.Item active>{page}</Pagination.Item>
                      <Pagination.Next
                        disabled={page === totalPages}
                        onClick={() =>
                          setPage((p) => Math.min(totalPages, p + 1))
                        }
                      />
                      <Pagination.Last
                        disabled={page === totalPages}
                        onClick={() => setPage(totalPages)}
                      />
                    </Pagination>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
